import React from 'react'
import Prism from 'prismjs'

import { Editor } from 'slate-react'
import PugLanguage from './langs/pug'

Prism.languages.pug = PugLanguage

/**
 * Define our code components.
 *
 * @param {Object} props
 * @return {Element}
 */

function CodeBlock (props) {
  return (
    <div style={{ position: 'relative' }}>
      <pre>
        <code className={props.className} {...props.attributes}>
          {props.children}
        </code>
      </pre>
    </div>
  )
}

function CodeBlockLine (props) {
  return <div {...props.attributes}>{props.children}</div>
}

/**
 * A helper function to return the content of a Prism `token`.
 *
 * @param {Object} token
 * @return {String}
 */

function getContent (token) {
  if (typeof token === 'string') {
    return token
  } else if (typeof token.content === 'string') {
    return token.content
  } else {
    return token.content.map(getContent).join('')
  }
}

/**
 * The code highlighting example.
 *
 * @type {Component}
 */

class CodeHighlighting extends React.Component {
  /**
   * Render.
   *
   * @return {Component}
   */

  render () {
    return (
      <CodeBlock className={this.props.codeClassName}>
        <Editor
          value={this.props.value}
          onKeyDown={this.onKeyDown}
          renderNode={this.renderNode}
          renderMark={this.renderMark}
          spellCheck={false}
          className={this.props.editorClassName}
          onChange={this.props.onChange}
        />
      </CodeBlock>
    )
  }

  /**
   * Render a Slate node.
   *
   * @param {Object} props
   * @return {Element}
   */

  renderNode (props, editor, next) {
    switch (props.node.type) {
      case 'line':
        return <CodeBlockLine {...props} />
      default:
        return next()
    }
  }

  /**
   * Render a Slate mark.
   *
   * @param {Object} props
   * @return {Element}
   */

  renderMark (props, editor, next) {
    const { children, mark, attributes } = props
    switch (mark.type) {
      case 'number':
        return (
          <span {...attributes} style={{ color: `#d19a66` }}>
            {children}
          </span>
        )
      case 'string':
        return (
          <span {...attributes} style={{ color: `#98c379` }}>
            {children}
          </span>
        )
      case 'function':
        return (
          <span {...attributes} style={{ color: `#61afef` }}>
            {children}
          </span>
        )
      case 'punctuation':
        return (
          <span {...attributes} style={{ color: `#4b5263` }}>
            {children}
          </span>
        )
      case 'operator':
        return (
          <span {...attributes} style={{ color: `#c678dd` }}>
            {children}
          </span>
        )
      case 'comment':
        return (
          <span {...attributes} style={{ color: `#5c6370`, opacity: '0.33' }}>
            {children}
          </span>
        )
      case 'keyword':
        return (
          <span
            {...attributes}
            style={{ color: `#be5046`, fontWeight: 'bold' }}
          >
            {children}
          </span>
        )
      case 'tag':
        return (
          <span {...attributes} style={{ color: `white`, fontWeight: 'bold' }}>
            {children}
          </span>
        )
      default: {
        // console.log(mark.type)
        return next()
      }
    }
  }

  /**
   * On key down inside code blocks, insert soft new lines.
   *
   * @param {Event} event
   * @param {Editor} editor
   * @param {Function} next
   */

  onKeyDown (event, editor, next) {
    const { value } = editor
    const { startBlock } = value

    if (event.key === 'Enter' && startBlock.type === 'code') {
      editor.insertText('\n')
      return
    }

    next()
  }

  /**
   * Decorate code blocks with Prism.js highlighting.
   *
   * @param {Node} node
   * @return {Array}
   */

  decorateNode (node, editor, next) {
    const others = next() || []

    const language = 'html'
    const texts = node.getTexts().toArray()
    const string = texts.map(t => t.text).join('\n')
    const grammar = Prism.languages[language]
    const tokens = Prism.tokenize(string, grammar)
    const decorations = []
    let startText = texts.shift()
    let endText = startText
    let startOffset = 0
    let endOffset = 0
    let start = 0

    for (const token of tokens) {
      startText = endText
      startOffset = endOffset

      const content = getContent(token)
      const newlines = content.split('\n').length - 1
      const length = content.length - newlines
      const end = start + length

      let available = startText.text.length - startOffset
      let remaining = length

      endOffset = startOffset + remaining

      while (available < remaining && texts.length > 0) {
        endText = texts.shift()
        remaining = length - available
        available = endText.text.length
        endOffset = remaining
      }

      if (typeof token !== 'string') {
        const dec = {
          anchor: {
            key: startText.key,
            offset: startOffset
          },
          focus: {
            key: endText.key,
            offset: endOffset
          },
          mark: {
            type: token.type
          }
        }

        decorations.push(dec)
      }

      start = end
    }

    return [...others, ...decorations]
  }
}

/**
 * Export.
 */

export default CodeHighlighting



// WEBPACK FOOTER //
// ./src/components/Settings/Motd/partials/CodeEditor/index.js